/* eslint-disable import/no-mutable-exports */
import { firstParameterState, memorizeForNg } from '@Utils/Decorators/NgServices';
import * as AuthHelper from '@Services/Auth/AuthHelper';
import get from 'lodash/get';
import each from 'lodash/each';
import map from 'lodash/map';
import uniqWith from 'lodash/uniqWith';
import flatten from 'lodash/flatten';
import isUndefined from 'lodash/isUndefined';
import flow from 'lodash/fp/flow';
import clone from 'lodash/fp/clone';
import extend from 'lodash/fp/extend';
import value from 'lodash/fp/value';
import toArray from 'lodash/fp/toArray';
import sortBy from 'lodash/fp/sortBy';

import * as UserDatabasesSelectors from './UserDatabasesSelectors';
import { RCS_DATABASES, CI_DATABASES, PU_DATABASES, databasesLinksMap } from './UserDatabasesConstants';

let getAll;
let getListWithIds;
let getDatabaseById;
let getDefaultDatabase;
let getSeparatedDatabases;
let isDefaultDatabase;
let getUserSearchContexts;
let getDbName;
let isCIDatabase;
let isRCSDatabase;
let isPUDatabase;
let supportsReaction;
let supportsSupplierData;
let getDatabaseIdByPrefix;

if (!window.isTest) {
  const getAllNg = memorizeForNg(UserDatabasesSelectors.getAll);
  const getListWithIdsNg = memorizeForNg(UserDatabasesSelectors.getListWithIds);
  const getDatabaseByIdNg = firstParameterState(UserDatabasesSelectors.getDatabaseById);
  const getDefaultDatabaseNg = memorizeForNg(UserDatabasesSelectors.getDefaultDatabase);
  const getSeparatedDatabasesNg = memorizeForNg(UserDatabasesSelectors.getSeparatedDatabases);
  const isDefaultDatabaseNg = firstParameterState(UserDatabasesSelectors.isDefaultDatabase);
  const getUserSearchContextsNg = memorizeForNg(UserDatabasesSelectors.getUserSearchContexts);
  const getDbNameNg = firstParameterState(UserDatabasesSelectors.getDbName);
  const isCIDatabaseNg = firstParameterState(UserDatabasesSelectors.isCIDatabase);
  const isRCSDatabaseNg = firstParameterState(UserDatabasesSelectors.isRCSDatabase);
  const isPUDatabaseNg = firstParameterState(UserDatabasesSelectors.isPUDatabase);
  const supportsReactionNg = firstParameterState(UserDatabasesSelectors.supportsReaction);
  const supportsSupplierDataNg = firstParameterState(UserDatabasesSelectors.supportsSupplierData);
  const getDatabaseIdByPrefixNg = firstParameterState(UserDatabasesSelectors.getDatabaseIdByPrefix);

  getAll = () => {
    return getAllNg();
  };
  getListWithIds = () => {
    return getListWithIdsNg();
  };
  getDatabaseById = (id) => {
    const db = getDatabaseByIdNg({ id });
    return db && db.toJS();
  };
  getDefaultDatabase = () => {
    return getDefaultDatabaseNg();
  };
  getSeparatedDatabases = () => {
    return getSeparatedDatabasesNg();
  };
  isDefaultDatabase = (id) => {
    return isDefaultDatabaseNg({ id });
  };
  getUserSearchContexts = () => {
    return getUserSearchContextsNg();
  };
  getDbName = (id) => {
    return getDbNameNg({ id });
  };
  isCIDatabase = (id) => {
    return isCIDatabaseNg({ id });
  };
  isRCSDatabase = (id) => {
    return isRCSDatabaseNg({ id });
  };
  isPUDatabase = (id) => {
    return isPUDatabaseNg({ id });
  };
  supportsReaction = (id) => {
    return supportsReactionNg({ id });
  };
  supportsSupplierData = (id) => {
    return supportsSupplierDataNg({ id });
  };
  getDatabaseIdByPrefix = (prefix) => {
    return getDatabaseIdByPrefixNg({ prefix });
  };
} else {
  // TODO: REMOVE this once angularJs is removed. The top block is the right code, else block is for Karma tests
  // eslint-disable-next-line no-inner-declarations
  function _getDatabasesObject() {
    return get(AuthHelper.getUser(), 'details.databases');
  }

  getAll = () => {
    const SORT_BY = 'priority';
    let databases = _getDatabasesObject();

    if (!databases) {
      Logger.error("UserDatabases: Current user doesn't have databases param.");
      databases = {};
    }
    return flow([toArray(), sortBy(SORT_BY), value()])(databases);
  };

  getListWithIds = () => map(getAll() || [], 'id');

  // eslint-disable-next-line func-names
  getDatabaseById = function (id) {
    const databases = _getDatabasesObject() || {};
    if (!databases[id]) {
      Logger.error(`UserDatabases: Database \`${id}\` does not exist.`);
      return null;
    }
    return databases[id];
  };

  // eslint-disable-next-line no-inner-declarations
  function _getDefaultDatabaseId() {
    const reaxysId = get(AuthHelper.getUser(), 'details.reaxysId');

    if (isUndefined(reaxysId) || reaxysId === null) {
      Logger.error('UserDatabases: ReaxysId param is undefined.');
    }

    return reaxysId;
  }

  getDefaultDatabase = () => getDatabaseById(_getDefaultDatabaseId());

  isDefaultDatabase = (id) => id === _getDefaultDatabaseId();

  getSeparatedDatabases = () => {
    const listOfDBs = [];
    each(getAll(), (db) =>
      each(db.description.split(/, ?/), (name) => {
        listOfDBs.push(
          flow([
            clone(),
            extend({
              description: name,
              hubPageURL: db.hubPageURLs[name] || db.hubPageURLs[databasesLinksMap[name]] || '',
              tooltip: db.tooltips[name] || db.tooltips[databasesLinksMap[name]] || '',
            }),
            value(),
          ])(db)
        );
      })
    );
    return listOfDBs;
  };

  getUserSearchContexts = () => uniqWith(flatten(map(getAll(), 'searchContexts')), (context) => context.serverAlias);

  getDbName = (databaseId) => {
    const db = getDatabaseById(databaseId);
    return db ? db.name : '';
  };

  isCIDatabase = (databaseId) => CI_DATABASES.includes(databaseId);
  isRCSDatabase = (databaseId) => RCS_DATABASES.includes(databaseId);
  isPUDatabase = (databaseId) => PU_DATABASES.includes(databaseId);
  supportsReaction = (databaseId) => getDatabaseById(databaseId).availableContexts.some((context) => context.serverAlias === 'reactions');
  supportsSupplierData = (databaseId) => ['5', '15', '16', '18'].indexOf(databaseId) !== -1;
}

export {
  getAll,
  getListWithIds,
  getDatabaseById,
  getDefaultDatabase,
  getSeparatedDatabases,
  isDefaultDatabase,
  getUserSearchContexts,
  getDbName,
  isCIDatabase,
  isRCSDatabase,
  isPUDatabase,
  supportsReaction,
  supportsSupplierData,
  getDatabaseIdByPrefix,
};
